import { createBrowserRouter, Navigate, Outlet, type RouteObject, RouterProvider } from "react-router";
import { isValidElement, useEffect, useMemo, type ReactNode } from "react";
import { ErrorPage } from "@/views/pages/error/ErrorPage";
import { LoginPage } from "@/views/pages/login/LoginPage";
import { AdminLayout } from "@/views/layouts/AppLayout";
import { systemManagentRoutes } from "./modules/systemManagent";
import { manageCenterRoutes } from "./modules/manageCenter";
import { userOperationRoutes } from "./modules/userOperation";
import { supplierManagementRoutes } from "./modules/supplierManagement";
import { productManagementRoutes } from "./modules/productManagement";
import { activityProductDetailHistoryRoutes, productActivityRoutes } from "./modules/productActivity";
import { tradeManageRoutes } from "./modules/tradeManage";
import { intelligentAssistantRoutes } from "./modules/intelligentAssistant";
import { lianYunUserRoutes } from "./modules/lianYunUser";
import { MyTaskPage } from "@/views/pages/myTask/MyTaskPage";
import { MyTaskNewsListPage } from "@/views/pages/myTask/MyTaskNewsListPage";
import { logisticsRoutes } from "./modules/logistics";
import { expenseManagementRoutes } from "./modules/expenseManagement";
import { indexResearchRoutes } from "./modules/indexResearch";
import { purchasingManageRoutes } from "./modules/purchasingManage";
import { dashboardRoutes } from "./modules/dashboard";
import { useUserStore } from "@/stores/react/authStore";
import { buildMenuRoutes, getFirstVisibleMenuPath } from "./menuRoutes";

export interface AdminRouteHandle {
    title?: string;
    permissions?: string[];
    isKeepAlive?: boolean;
    /** 隐藏详情、表单等页面进入后，需要保持高亮的可见菜单完整路径。 */
    activeMenu?: string;
}

export type AdminRouteObject = RouteObject & {
    handle?: AdminRouteHandle;
    children?: AdminRouteObject[];
};

/** 沿用旧 Vue 的标题规则：优先使用最深层路由标题，没有标题时回退到系统名。 */
export function resolveAdminDocumentTitle(routeTitles: Array<string | undefined>): string {
    // 子路由标题覆盖父级菜单标题，与旧版 to.meta.title 的匹配结果一致。
    const pageTitle = [...routeTitles]
        .reverse()
        .map(title => title?.trim())
        .find(Boolean);
    return pageTitle ? `${pageTitle} - 链云砂石` : "链云砂石";
}

/**
 * 还原旧 Vue 根路由守卫：登录用户访问 `/` 时进入第一个有权限的可见菜单页面。
 * 目标从同一份菜单树计算，确保默认入口与头部、侧栏的权限和隐藏规则一致。
 */
export function DefaultRouteRedirect() {
    const permissions = useUserStore(state => state.userInfo.permissions || []);
    const targetPath = getFirstVisibleMenuPath(buildMenuRoutes(staticRoutes, permissions)) || "/403";

    return <Navigate replace to={targetPath} />;
}

/** 模板结构路由入口，后续按一级业务模块拆分至 views/router/modules。 */
export const staticRoutes: AdminRouteObject[] = [
    { path: "/", element: <DefaultRouteRedirect />, handle: { title: "首页" } },
    {
        path: "/myTask",
        element: <AdminLayout />,
        handle: { title: "待办工作", permissions: ["ROLE_OUTSTANDING_R"] },
        children: [
            {
                // 沿用 Vue 根路由 redirect，进入待办工作时落到历史 index 地址。
                index: true,
                element: <Navigate replace to="index" />,
                handle: { title: "待办工作", permissions: ["ROLE_OUTSTANDING_R"] }
            },
            // 保留 Vue /myTask/index 历史入口。
            {
                path: "index",
                element: <MyTaskPage />,
                handle: { title: "待办工作", permissions: ["ROLE_OUTSTANDING_R"] }
            },
            {
                path: "newsList",
                element: <MyTaskNewsListPage />,
                handle: { title: "动态列表", permissions: ["ROLE_OUTSTANDING_R"] }
            }
        ]
    },
    { path: "/login", element: <LoginPage />, handle: { title: "登录" } },
    dashboardRoutes,
    systemManagentRoutes,
    manageCenterRoutes,
    userOperationRoutes,
    supplierManagementRoutes,
    productManagementRoutes,
    productActivityRoutes,
    activityProductDetailHistoryRoutes,
    tradeManageRoutes,
    logisticsRoutes,
    expenseManagementRoutes,
    purchasingManageRoutes,
    intelligentAssistantRoutes,
    lianYunUserRoutes,
    indexResearchRoutes,
    { path: "/403", element: <ErrorPage status={403} />, handle: { title: "403页面" } },
    { path: "/404", element: <ErrorPage status={404} />, handle: { title: "404页面" } },
    { path: "/500", element: <ErrorPage status={500} />, handle: { title: "500页面" } },
    { path: "*", element: <Navigate replace to="/404" /> }
];

const hasRoutePermission = (route: AdminRouteObject, permissions: string[]) => {
    const required = route.handle?.permissions || [];
    return required.length === 0 || required.some((permission: string) => permissions.includes(permission));
};

/** 递归过滤动态菜单，沿用 Vue 版本任一权限命中规则。 */
export const filterDynamicRoutes = (routes: AdminRouteObject[], permissions: string[]): AdminRouteObject[] =>
    routes.reduce<AdminRouteObject[]>((result, route) => {
        const children = route.children ? filterDynamicRoutes(route.children, permissions) : [];
        if ((route.children && children.length > 0) || (!route.children && hasRoutePermission(route, permissions))) {
            result.push(route.children ? { ...route, children } : route);
        }
        return result;
    }, []);

export interface RouteAccessGuardProps {
    permissions?: string[];
    children: ReactNode;
}

/** React 路由守卫：沿用 Vue 的登录拦截和任一权限命中规则。 */
export function RouteAccessGuard({ permissions = [], children }: RouteAccessGuardProps) {
    const token = useUserStore(state => state.token);
    const userPermissions = useUserStore(state => state.userInfo.permissions || []);

    if (!token) {
        return <Navigate replace to="/login" />;
    }
    if (permissions.length > 0 && !permissions.some(permission => userPermissions.includes(permission))) {
        return <Navigate replace to="/403" />;
    }
    return <>{children}</>;
}

/** 登录与错误页沿用 Vue 白名单，未登录用户也必须能够访问这些公开入口。 */
const publicRoutePaths = new Set(["/login", "/403", "/404", "/500", "*"]);

/** 在真正创建 data router 前为页面元素注入权限守卫，避免只过滤菜单而放开直达 URL。 */
export const withRouteAccessGuards = (routes: AdminRouteObject[]): AdminRouteObject[] =>
    routes.map(route => {
        const element =
            route.element && !publicRoutePaths.has(route.path || "") ? (
                <RouteAccessGuard permissions={route.handle?.permissions}>{route.element}</RouteAccessGuard>
            ) : (
                route.element
            );
        return route.children
            ? { ...route, element, children: withRouteAccessGuards(route.children) }
            : { ...route, element };
    });

/** 判断模块路由是否仍携带迁移期重复声明的后台布局。 */
const usesAdminLayout = (route: AdminRouteObject) => isValidElement(route.element) && route.element.type === AdminLayout;

/**
 * 把各业务模块重复声明的 AdminLayout 合并为单一缓存宿主。
 * 模块自身的权限和标题层级继续由中间 Outlet 保留，公开页仍位于后台布局之外。
 */
export function composeAdminLayoutRoutes(routes: AdminRouteObject[]): AdminRouteObject[] {
    const adminChildren: AdminRouteObject[] = [];
    const standaloneRoutes: AdminRouteObject[] = [];
    const fallbackRoutes: AdminRouteObject[] = [];

    for (const route of routes) {
        if (route.path === "*") {
            fallbackRoutes.push(route);
        } else if (usesAdminLayout(route)) {
            adminChildren.push({ ...route, element: <Outlet /> });
        } else {
            standaloneRoutes.push(route);
        }
    }

    return [
        ...standaloneRoutes,
        ...(adminChildren.length
            ? [{ id: "admin-layout", element: <AdminLayout />, children: adminChildren } satisfies AdminRouteObject]
            : []),
        ...fallbackRoutes
    ];
}

/** 创建浏览器路由实例，供应用入口和测试注入动态路由。 */
export const createAdminRouter = (dynamicRoutes: AdminRouteObject[] = []) =>
    createBrowserRouter(
        withRouteAccessGuards(
            composeAdminLayoutRoutes([
                ...staticRoutes.slice(0, -1),
                ...dynamicRoutes,
                staticRoutes[staticRoutes.length - 1]!
            ])
        ),
        {
            basename: import.meta.env.VITE_PUBLIC_PATH || "/"
        }
    );

/** 把当前路由层级上的标题同步到浏览器标签，并监听后续导航变化。 */
export function subscribeAdminDocumentTitle(router: ReturnType<typeof createAdminRouter>): () => void {
    // 最深层页面标题优先；路由没有标题时回退到旧 Vue 使用的系统名称。
    const syncDocumentTitle = () => {
        document.title = resolveAdminDocumentTitle(
            router.state.matches.map(match => (match.route as AdminRouteObject).handle?.title)
        );
    };

    syncDocumentTitle();
    return router.subscribe(syncDocumentTitle);
}

// 静态默认值保持稳定，避免 AdminRouterProvider 重渲染时意外重建浏览器路由。
const EMPTY_DYNAMIC_ROUTES: AdminRouteObject[] = [];

export function AdminRouterProvider({
    dynamicRoutes = EMPTY_DYNAMIC_ROUTES
}: {
    dynamicRoutes?: AdminRouteObject[];
}): ReactNode {
    // 重用路由实例，保证文档标题订阅与当前导航状态保持一致。
    const router = useMemo(() => createAdminRouter(dynamicRoutes), [dynamicRoutes]);

    // React 路由的 handle.title 对齐 Vue 的 meta.title，并随导航更新浏览器标签。
    useEffect(() => subscribeAdminDocumentTitle(router), [router]);

    return <RouterProvider router={router} />;
}
